import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import FaceTimePublicPage from "@/pages/FaceTimePublicPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import MembersPage from "@/pages/MembersPage";
import { lazyRoute } from "@/lib/lazyRoute";

// Lazy-load secondary + admin pages to keep the initial bundle small on mobile.
const BillingPage = lazyRoute(() => import("@/pages/BillingPage"));
const AppointmentsPage = lazyRoute(() => import("@/pages/AppointmentsPage"));
const SettingsPage = lazyRoute(() => import("@/pages/SettingsPage"));
const OnboardingPage = lazyRoute(() => import("@/pages/OnboardingPage"));
const AdminAnalyticsPage = lazyRoute(() => import("@/pages/AdminAnalyticsPage"));
const MemberAnalyticsPage = lazyRoute(() => import("@/pages/MemberAnalyticsPage"));
const LeaderboardPage = lazyRoute(() => import("@/pages/LeaderboardPage"));
const FindLeadsPage = lazyRoute(() => import("@/pages/FindLeadsPage"));
const LeadRequestsPage = lazyRoute(() => import("@/pages/LeadRequestsPage"));
const SuperAdminPerformancePage = lazyRoute(() => import("@/pages/SuperAdminPerformancePage"));
const MessagesPage = lazyRoute(() => import("@/pages/MessagesPage"));
const SmsInboxPage = lazyRoute(() => import("@/pages/SmsInboxPage"));
const GroupsPage = lazyRoute(() => import("@/pages/GroupsPage"));
const AssignmentRulesPage = lazyRoute(() => import("@/pages/AssignmentRulesPage"));
const CRMImportPage = lazyRoute(() => import("@/pages/CRMImportPage"));
const WebhooksPage = lazyRoute(() => import("@/pages/WebhooksPage"));
const EmailPage = lazyRoute(() => import("@/pages/EmailPage"));
const CampaignsPage = lazyRoute(() => import("@/pages/CampaignsPage"));
const UserManualPage = lazyRoute(() => import("@/pages/UserManualPage"));
import MorePage from "@/pages/MorePage";
const ReportsPage = lazyRoute(() => import("@/pages/ReportsPage"));
const SupportPage = lazyRoute(() => import("@/pages/SupportPage"));
const AuditLogPage = lazyRoute(() => import("@/pages/AuditLogPage"));
const FreeAccountsPage = lazyRoute(() => import("@/pages/FreeAccountsPage"));
const CodeQualityPage = lazyRoute(() => import("@/pages/CodeQualityPage"));
const AdminCouponsPage = lazyRoute(() => import("@/pages/AdminCouponsPage"));
const SmsCampaignPage = lazyRoute(() => import("@/pages/admin/SmsCampaignPage"));
const SmsConsentLogPage = lazyRoute(() => import("@/pages/admin/SmsConsentLogPage"));
const SmsDripPage = lazyRoute(() => import("@/pages/admin/SmsDripPage"));
const MarketingEmailPage = lazyRoute(() => import("@/pages/admin/MarketingEmailPage"));
const AdminSystemPage = lazyRoute(() => import("@/pages/admin/SystemDashboardPage"));
const CollaborationAnalyticsPage = lazyRoute(() => import("@/pages/admin/CollaborationAnalyticsPage"));
const MarketingVideosPage = lazyRoute(() => import("@/pages/admin/MarketingVideosPage"));
const ChangelogPage = lazyRoute(() => import("@/pages/admin/ChangelogPage"));
const GroupDiscoveryPage = lazyRoute(() => import("@/pages/GroupDiscoveryPage"));
const GuestVideoPage = lazyRoute(() => import("@/pages/GuestVideoPage"));
const GuestVideoRoomsPage = lazyRoute(() => import("@/pages/GuestVideoRoomsPage"));
import SmsTermsPage from "@/pages/SmsTermsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import SupportChatBubble from "@/components/SupportChatBubble";
import { TourProvider } from "@/contexts/TourContext";
import { TourOverlay } from "@/components/TourOverlay";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import BottomNav from "@/components/BottomNav";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { MessageBubbleToaster } from "@/components/MessageBubbleToaster";
import { CallProvider } from "@/contexts/CallContext";
import { CallOverlay } from "@/components/calls/CallOverlay";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { SmsConsentBanner } from "@/components/SmsConsentBanner";
import VersionCheck from "@/components/VersionCheck";

const ROLES_SUPER_ADMIN = ['super_admin'];
const ROLES_ADMIN = ['admin'];

function AppLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col">
      <ImpersonationBanner />
      <SmsConsentBanner />
      <Navbar />
      <div className="flex-1 pb-20 lg:pb-0">{children}</div>
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-4 px-4 sm:px-6 hidden lg:block">
        <p className="text-center text-[11px] text-slate-400">&copy; {new Date().getFullYear()} Encore Business Services Group LLC. All rights reserved.</p>
      </footer>
      <BottomNav />
      <OfflineIndicator />
      <MessageBubbleToaster />
      <CallOverlay />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
        <WebSocketProvider>
        <CallProvider>
        <TourProvider>
        <Toaster position="top-right" />
        <VersionCheck />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/ft/:slug" element={<FaceTimePublicPage />} />
          <Route path="/sms-terms" element={<SmsTermsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
          <Route path="/leads" element={<ProtectedRoute><AppLayout><LeadsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/leads/:id" element={<ProtectedRoute><AppLayout><LeadDetailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/members" element={<ProtectedRoute><AppLayout><MembersPage /></AppLayout></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><AppLayout><MessagesPage /></AppLayout></ProtectedRoute>} />
          <Route path="/sms-inbox" element={<ProtectedRoute><AppLayout><SmsInboxPage /></AppLayout></ProtectedRoute>} />
          <Route path="/email" element={<ProtectedRoute><AppLayout><EmailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/groups" element={<ProtectedRoute><AppLayout><GroupsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/groups/discover" element={<ProtectedRoute><AppLayout><GroupDiscoveryPage /></AppLayout></ProtectedRoute>} />
          <Route path="/video/guest-rooms" element={<ProtectedRoute><AppLayout><GuestVideoRoomsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/video/guest/:roomId" element={<GuestVideoPage />} />
          <Route path="/appointments" element={<ProtectedRoute><AppLayout><AppointmentsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AppLayout><MemberAnalyticsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><AppLayout><LeaderboardPage /></AppLayout></ProtectedRoute>} />
          <Route path="/find-leads" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><FindLeadsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/lead-requests" element={<ProtectedRoute><AppLayout><LeadRequestsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/lead-requests/performance" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><SuperAdminPerformancePage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><AdminAnalyticsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/assignment-rules" element={<ProtectedRoute roles={ROLES_ADMIN}><AppLayout><AssignmentRulesPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/webhooks" element={<ProtectedRoute roles={ROLES_ADMIN}><AppLayout><WebhooksPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/campaigns" element={<ProtectedRoute roles={ROLES_ADMIN}><AppLayout><CampaignsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/import/crm" element={<ProtectedRoute><AppLayout><CRMImportPage /></AppLayout></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><AppLayout><UserManualPage /></AppLayout></ProtectedRoute>} />
          <Route path="/more" element={<ProtectedRoute><AppLayout><MorePage /></AppLayout></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><SupportPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/free-accounts" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><FreeAccountsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/audit-log" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><AuditLogPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/code-quality" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><CodeQualityPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/coupons" element={<ProtectedRoute roles={ROLES_ADMIN}><AppLayout><AdminCouponsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/sms-campaign" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><SmsCampaignPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/sms-consent-log" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><SmsConsentLogPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/sms-drip" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><SmsDripPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/marketing-email" element={<ProtectedRoute roles={ROLES_SUPER_ADMIN}><AppLayout><MarketingEmailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/system" element={<ProtectedRoute roles={ROLES_ADMIN}><AppLayout><AdminSystemPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/collaboration" element={<ProtectedRoute roles={ROLES_ADMIN}><AppLayout><CollaborationAnalyticsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/marketing-videos" element={<ProtectedRoute roles={ROLES_ADMIN}><AppLayout><MarketingVideosPage /></AppLayout></ProtectedRoute>} />
          <Route path="/admin/changelog" element={<ProtectedRoute roles={ROLES_ADMIN}><AppLayout><ChangelogPage /></AppLayout></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><AppLayout><BillingPage /></AppLayout></ProtectedRoute>} />
        </Routes>
        <SupportChatBubble />
        <TourOverlay />
        </TourProvider>
        </CallProvider>
        </WebSocketProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
